import React, { useState } from "react";
import Body from "../../layouts/Body";
import MUIDataTable from "mui-datatables";
import { Button, IconButton, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ScheduleForm from "./ScheduleForm";
import { useMenteeContext } from "../../context/MenteeContext";
import { scheduleTypes, statusType } from "../../constants/constants";
import ActionItem from "./ActionItem";
import moment from "moment/moment";
import { Cached } from "@mui/icons-material";

const Schedule = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openActionItem, setOpenActionItem] = useState(false);
  const { menteeList, getSelectedMenteeData, getMenteeMappingAndData } =
    useMenteeContext();

  // handle click of schedule, observation and action item button
  const handleButtonClick = (menteeId, buttonType) => {
    // get the data of selected mentee and save in the mentee variable
    getSelectedMenteeData(menteeId);

    // open corresponding popup's or navigate... according to button which is clicked
    if (buttonType == "schedule") {
      setOpen(true);
    } else if (buttonType == "observation") {
      navigate("/observation");
    } else if (buttonType == "actionItem") {
      setOpenActionItem(true);
    }
  };

  const handleScheduleDisable = (rowData) => {
    if (!rowData[2] || !rowData[3]) {
      return true;
    }

    let today = new Date();
    let scheduleDate = new Date(rowData[5]);

    let disabled =
      today.toLocaleDateString() === scheduleDate.toLocaleDateString()
        ? false
        : today.getTime() > scheduleDate.getTime();
    return [statusType.Completed, "Observed"].includes(rowData[7]) || disabled;
  };

  const handleObservationDisable = (rowData) => {
    if (!rowData[2] || !rowData[3]) {
      return true;
    }

    let today = new Date();
    let scheduleDate = new Date(rowData[5]);
    scheduleDate.setDate(scheduleDate.getDate() + 5);

    let disabled =
      today.toLocaleDateString() === scheduleDate.toLocaleDateString()
        ? false
        : today.getTime() > scheduleDate.getTime();

    return (
      [statusType.Pending, "Observed", statusType.Completed].includes(
        rowData[7]
      ) ||
      rowData[6] === "Mentoring" ||
      disabled
    );
  };

  const handleActionItemDisable = (rowData) => {
    if (!rowData[2] || !rowData[3]) {
      return true;
    }

    let today = new Date();
    let scheduleDate = new Date(rowData[5]); // scheduleDate at index 5
    scheduleDate.setDate(scheduleDate.getDate() + 5);

    let disabled =
      today.toLocaleDateString() === scheduleDate.toLocaleDateString()
        ? false
        : today.getTime() > scheduleDate.getTime();

    return [statusType.Pending, statusType.Completed].includes(rowData[7])
      ? true
      : (rowData[6] === "Observation" && rowData[7] !== "Observed") || disabled;
  };

  const getButtonTooltip = (rowData) => {
    if (!rowData[2] || !rowData[3]) {
      return "Add Primary Domain and Specilization to enable the button.";
    }
    return "";
  };

  // columns of the table
  const columns = [
    // mentee id column, cannot filter, displat and sort
    {
      name: "id",
      label: "Id",
      options: {
        filter: false,
        sort: false,
        display: false,
      },
    },
    {
      name: "name",
      label: "Name",
    },
    {
      name: "primaryDomain",
      label: "Primary Domain Type",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "specialization",
      label: "YDM or Domain Specialization",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "programName",
      label: "Program Name",
    },
    {
      name: "scheduleDate",
      label: "Schedule Date",
      options: {
        customBodyRender: (value) =>
          value ? moment(value).format("YYYY-MM-DD") : "",
      },
    },
    {
      name: "scheduleType",
      label: "Schedule Type",
      options: {
        customBodyRender: (value) => scheduleTypes[value],
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        customBodyRender: (value) => statusType[value],
      },
    },
    {
      name: "schedule",
      label: "Schedule",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <Tooltip title={getButtonTooltip(tableMeta.rowData)}>
              <span>
                <Button
                  disabled={handleScheduleDisable(tableMeta.rowData)}
                  onClick={() =>
                    handleButtonClick(tableMeta.rowData[0], "schedule")
                  }
                >
                  {tableMeta.rowData[6] ? "Reschedule" : "Schedule"}
                </Button>
              </span>
            </Tooltip>
          );
        },
        filter: false,
        sort: false,
        download: false,
      },
    },
    {
      name: "Observation",
      label: "Observation",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <Tooltip title={getButtonTooltip(tableMeta.rowData)}>
              <span>
                <Button
                  disabled={handleObservationDisable(tableMeta.rowData)}
                  onClick={() =>
                    handleButtonClick(tableMeta.rowData[0], "observation")
                  }
                >
                  Capture CO
                </Button>
              </span>
            </Tooltip>
          );
        },
        filter: false,
        sort: false,
        download: false,
      },
    },
    {
      name: "action",
      label: "Action Items",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <Tooltip title={getButtonTooltip(tableMeta.rowData)}>
              <span>
                <Button
                  disabled={handleActionItemDisable(tableMeta.rowData)}
                  onClick={() =>
                    handleButtonClick(tableMeta.rowData[0], "actionItem")
                  }
                >
                  Action Items
                </Button>
              </span>
            </Tooltip>
          );
        },
        filter: false,
        sort: false,
        download: false,
      },
    },
  ];

  return (
    <Body>
      <Typography
        variant="h5"
        className="page-title text-centered"
        color="primary"
      >
        Schedule Mentoring / Classroom Observation (CO)
      </Typography>
      <MUIDataTable
        columns={columns}
        data={menteeList}
        title="Facilitator List"
        options={{
          selectableRows: "none", // <===== will turn off checkboxes in rows
          print: false,
          viewColumns: false,
          customToolbar: function () {
            // <===== will display refresh icon on toolbar
            return (
              <IconButton onClick={getMenteeMappingAndData}>
                <Cached />
              </IconButton>
            );
          },
        }}
      />

      <ScheduleForm open={open} setOpen={setOpen} />
      <ActionItem open={openActionItem} setOpen={setOpenActionItem} />
    </Body>
  );
};

export default Schedule;
